import { Children, ReactElement } from 'react';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Hidden from '@material-ui/core/Hidden';

import { Form, Formik, FormikConfig, FormikValues } from 'formik';
import useStepper from '../../../hooks/useStepper';
import { StyledConnector, CustomStep } from './StyledStepper';
import { ArrowForwardIcon, CalendarScheduledIcon, WorkspaceIcon, BuildingIcon } from '../../../assets/icons';
import useStyles from './styles';
import useCommonStyles, { blueButtonProps } from '../../../styles/common';

export interface FormikStepProps
  extends Pick<FormikConfig<FormikValues>, 'children' | 'validationSchema'> {
  label: string;
}

export const FormikStep = ({ children }: FormikStepProps) => <>{children}</>;

export const FormikStepper = ({ children, ...props }: FormikConfig<FormikValues>) => {
  const { step, setStep, editMode, setEditMode } = useStepper();

  const childrenArray = Children.toArray(children) as ReactElement<FormikStepProps>[];
  const currentChild = childrenArray[step];
  const isLastStep = () => step === childrenArray.length - 1;
  const classes = useStyles({ isLastStep: isLastStep() });
  const commonClasses = useCommonStyles();

  const icons: { [index: string]: React.ReactElement } = {
    0: <img src={CalendarScheduledIcon} alt="CalendarScheduledIcon" draggable="false" />,
    1: <img src={WorkspaceIcon} alt="WorkspaceIcon" draggable="false" />,
    2: <img src={BuildingIcon} alt="BuildingIcon" draggable="false" />,
    3: <img src={CalendarScheduledIcon} alt="CalendarScheduledIcon" draggable="false" />,
  };

  return (
    <Formik
      {...props}
      validationSchema={currentChild.props.validationSchema}
      onSubmit={async (values, helpers) => {
        if (editMode) {
          setEditMode(false);
          setStep(childrenArray.length - 1);
          return;
        };
        if (isLastStep()) await props.onSubmit(values, helpers);
        else setStep((s) => s + 1);
      }}
    >

      {({ isSubmitting/* , values */ }) => (
        <Form autoComplete="off" className={classes.form}>
          <Hidden only="xs">
            <Typography variant="h2"> {!isLastStep() ? 'Novo Agendamento' : 'Resumo do Agendamento'}</Typography>
          </Hidden>

          <Hidden only="xs">
            <Stepper alternativeLabel activeStep={step} connector={<StyledConnector />}>
              {childrenArray.map((child, index) => (
                <Step key={`step-${index}`} completed={step > index}>
                  <StepLabel StepIconComponent={CustomStep} >{child.props.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Hidden>

          <Hidden smUp>
            <Box className={classes.iconContainer}>
              {icons[String(step)]}
            </Box>
          </Hidden>

          {currentChild}

          <Container maxWidth="xs">
            <Grid container spacing={2} className={classes.buttonsContainer}>
              {!editMode ? (
                <>
                  {step > 0 && (
                    <Grid container item xs={6} justify="center">
                      <Button
                        disabled={isSubmitting}
                        variant="contained"
                        onClick={() => setStep((s) => s - 1)}
                        disableElevation
                      >
                        Voltar
                      </Button>
                    </Grid>
                  )}
                  <Grid container item xs={6} justify="center">
                    <Button
                      data-cy="go-forward-button"
                      type="submit"
                      startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
                      endIcon={<ArrowForwardIcon />}
                      disabled={isSubmitting}
                      className={commonClasses.blueButton}
                      {...blueButtonProps}
                    >
                      {isSubmitting ? 'Enviando...' : isLastStep() ? 'Finalizar' : 'Continuar'}
                    </Button>
                  </Grid>
                </>
              ) : (
                <Button
                  data-ty="save-changes-button"
                  type="submit"
                  className={commonClasses.blueButton}
                  {...blueButtonProps}
                >
                  Salvar Alterações
                </Button>
              )}

            </Grid>
          </Container>
        </Form>
      )}
    </Formik>
  );
}